import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  IFiltersBar,
  IFiltersList,
  ISearchFilter,
  TFieldFilter,
  TFieldValidation,
  TInputFilter,
} from '../../../types/data_table';

/** How long typing pauses before a search filter reaches the table. */
const SEARCH_DEBOUNCE_MS = 1000;

export interface IUseFieldFilters {
  filtersList: IFiltersList;
  /** Text and phone filters, in the order the bar renders them. */
  inputFilters: TInputFilter[];
  onChange: IFiltersBar['filtersChangeHandler'];
}

/**
 * State for the filters the user types into.
 *
 * There are two kinds and they behave differently:
 *
 * - **text / phone** are edited in a pop-up and applied explicitly, then shown
 *   as a removable chip. `appliedFieldFilters` holds those chips.
 * - **search** is typed inline and applied on a debounce. `searchValues` holds
 *   what is in the box right now, which runs ahead of what the table has.
 */
export const useFieldFilters = ({
  filtersList,
  inputFilters,
  onChange,
}: IUseFieldFilters) => {
  /** Applied text/phone values, rendered as chips. */
  const [appliedFieldFilters, setAppliedFieldFilters] = useState<
    TFieldFilter[]
  >([]);

  /** Validation messages, keyed by filter id. */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /** Live search box contents, keyed by filter id. */
  const [searchValues, setSearchValues] = useState<Record<string, string>>({});

  const hasInitializedInputFilters = useRef(false);
  const debounceTimeouts = useRef<Record<string, NodeJS.Timeout>>({});

  /**
   * The value each search filter had applied the last time the list changed,
   * so a rebuilt list can be told apart from a genuinely new value.
   */
  const lastAppliedSearch = useRef<Record<string, string>>({});

  /**
   * The value last typed into each field, kept so it can be checked again
   * when the rule that governs it changes — picking a different country
   * swaps the pattern a phone number has to match.
   */
  const lastTypedValue = useRef<Record<string, string>>({});

  /** Adds or updates an applied chip. */
  const applyFieldFilter = (filter: TFieldFilter, value?: string) =>
    setAppliedFieldFilters((previous) =>
      previous.some((applied) => applied.id === filter.id)
        ? previous.map((applied) =>
            applied.id === filter.id ? { ...applied, value } : applied
          )
        : [...previous, { ...filter, value }]
    );

  /** Drops an applied chip. */
  const removeFieldFilter = (filter: TFieldFilter) =>
    setAppliedFieldFilters((previous) =>
      previous.filter((applied) => applied.id !== filter.id)
    );

  /** The applied value for a filter, or `''` when it has none. */
  const getAppliedValue = (id: string): string =>
    appliedFieldFilters.find((applied) => applied.id === id)?.value || '';

  /**
   * Runs a value against the filter's patterns and records the first failure.
   * An empty value is always allowed — nothing to validate yet.
   */
  const validateField = (
    value: string,
    id: string,
    validations?: TFieldValidation[]
  ) => {
    lastTypedValue.current[id] = value;

    for (const validation of validations || []) {
      const hasFailed = !!value && !new RegExp(validation.pattern).test(value);

      setFieldErrors((previous) => ({
        ...previous,
        [id]: hasFailed ? validation.error_msg : '',
      }));

      if (hasFailed) return;
    }
  };

  /**
   * Re-runs validation on the value already typed into a field, against a new
   * set of rules.
   */
  const revalidateField = (id: string, validations?: TFieldValidation[]) =>
    validateField(lastTypedValue.current[id] ?? '', id, validations);

  /** Clears every validation message, e.g. when the pop-up is dismissed. */
  const clearFieldErrors = () => setFieldErrors({});

  // Drop any pending debounce when the bar goes away. Without this, leaving
  // the page mid-search updates an unmounted tree and fires a request for a
  // table nobody is looking at any more.
  useEffect(
    () => () => {
      Object.values(debounceTimeouts.current).forEach(clearTimeout);
      debounceTimeouts.current = {};
    },
    []
  );

  /**
   * Search filters update their box immediately and the table only once the
   * user stops typing, so a fast typist causes one request, not one per key.
   */
  const handleSearchChange = useCallback(
    (filter: ISearchFilter, value: string) => {
      setSearchValues((previous) => ({ ...previous, [filter.id]: value }));

      clearTimeout(debounceTimeouts.current[filter.id]);

      debounceTimeouts.current[filter.id] = setTimeout(() => {
        applyFieldFilter(filter, value);
        onChange(filter.id, filter.type, value);
      }, SEARCH_DEBOUNCE_MS);
    },
    [onChange]
  );

  /** Wipes local state after the container has reset the filters. */
  const resetFieldFilters = () => {
    setAppliedFieldFilters([]);
    setFieldErrors({});
    setSearchValues({});
    lastTypedValue.current = {};
  };

  // Follow the container's list: re-label the chips (the labels change with
  // the language) and re-seed the search boxes from it.
  useEffect(() => {
    if (appliedFieldFilters.length) {
      setAppliedFieldFilters((previous) => {
        const relabelled = previous.map((applied) => {
          const current = filtersList.find(
            (filter) => filter.id === applied.id
          );
          return current ? { ...applied, label: current.label } : applied;
        });

        const isUnchanged = relabelled.every(
          (chip, index) => chip.label === previous[index].label
        );

        return isUnchanged ? previous : relabelled;
      });
    }

    const searchFilters = filtersList.filter(
      (filter) => filter?.type === 'search'
    );

    /** What each search filter currently has *applied*. */
    const applied: Record<string, string> = {};
    searchFilters.forEach((filter) => {
      applied[filter.id] = filter.value == null ? '' : String(filter.value);
    });

    setSearchValues((previous) => {
      const next: Record<string, string> = {};

      searchFilters.forEach(({ id }) => {
        // Take the applied value only when it has actually changed — a reset,
        // a new language, a value restored from the URL. Otherwise keep what
        // is in the box, which may be newer than what has been applied: this
        // effect also runs when the list is merely rebuilt with identical
        // content, and overwriting there wiped whatever was being typed.
        const hasNewAppliedValue =
          applied[id] !== lastAppliedSearch.current[id];

        next[id] = hasNewAppliedValue
          ? applied[id]
          : previous[id] ?? applied[id];
      });

      const isUnchanged =
        Object.keys(next).length === Object.keys(previous).length &&
        Object.keys(next).every((id) => next[id] === previous[id]);

      return isUnchanged ? previous : next;
    });

    lastAppliedSearch.current = applied;
  }, [filtersList]);

  // A text or phone filter can arrive with a value already on it — a filter
  // restored from the URL, say. Show those as chips once.
  //
  // Only the chips: the container seeds the matching `params` itself, at the
  // moment it builds the list, so nothing here has to reach the table before
  // the first request goes out.
  useEffect(() => {
    if (hasInitializedInputFilters.current) return;
    if (!inputFilters?.length) return;

    hasInitializedInputFilters.current = true;

    inputFilters.forEach((filter) => {
      if (typeof filter.value !== 'string' || !filter.value) return;

      applyFieldFilter(filter, filter.value);
    });
  }, [inputFilters]);

  return {
    appliedFieldFilters,
    fieldErrors,
    searchValues,
    applyFieldFilter,
    removeFieldFilter,
    getAppliedValue,
    validateField,
    revalidateField,
    clearFieldErrors,
    handleSearchChange,
    resetFieldFilters,
  };
};
